import { useOnboardingAnalytics } from "analytics/onboarding";
import {
  BOT_CARD_HEIGHT,
  BOT_CARD_WIDTH,
  MOBILE_BOT_CARD_HEIGHT,
  MOBILE_BOT_CARD_WIDTH,
} from "@shared/features/onboarding/constants";
import VirtualCardsList from "common/Lists/VirtualCardsList";
import React, { useContext } from "react";
import { BotTemplateData, WorkspaceType } from "@shared/types";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import useQuery from "utils/hooks/useQuery";
import useOnboardingPracticeAccount from "features/onboarding/guided/useOnboardingPracticeAccount";
import {
  DcaBotsType,
  OnboardingDetailedStep,
} from "@shared/features/onboarding/types";
import PublicBotCard from "features/bots/PublicBots/PublicBotCard";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import styled, { useTheme } from "styled-components";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import useGetVirtualListCardDimensions from "utils/hooks/useGetVirtualListCardDimensions";
import {
  SECTION_TYPE_TO_TRADING_CATEGORY,
  TRADING_CATEGORY_TO_CATEGORY_CONST,
} from "features/onboarding/common/constants";

const ONBOARDING_BOT_CARD_WIDTH = BOT_CARD_WIDTH + 48;

interface DcaBotsSectionProps {
  botType: DcaBotsType;
  bots?: BotTemplateData[];
  isLoading?: boolean;
}

const DcaBotsSection = ({ botType, bots, isLoading }: DcaBotsSectionProps) => {
  const { isNewLook } = useTheme();
  const isPhone = useMedia(["(max-width: 560px)"], [true], false);
  const { cardHeight, cardWidth } = useGetVirtualListCardDimensions(
    BOT_CARD_HEIGHT,
    MOBILE_BOT_CARD_HEIGHT,
    ONBOARDING_BOT_CARD_WIDTH,
    MOBILE_BOT_CARD_WIDTH
  );

  // New-look phones: each section is a row of compact preset tiles that
  // scrolls sideways (the next one peeks in), so the sections fit a screen.
  if (isNewLook && isPhone)
    return (
      <PhoneSection>
        <PhoneSectionTitle>{botType}</PhoneSectionTitle>
        <LoadingSpinnerOverlay isLoading={!!isLoading && !bots?.length}>
          <PhoneTiles>
            {bots?.map((bot, index) => (
              <PhoneTile key={bot.id}>
                <PublicBotCardComponent
                  data={bot}
                  extraData={botType}
                  index={index}
                  asRow
                />
              </PhoneTile>
            ))}
          </PhoneTiles>
        </LoadingSpinnerOverlay>
      </PhoneSection>
    );

  return (
    <Wrapper>
      <VirtualCardsList
        cardsData={bots}
        extraCardsData={botType}
        loading={isLoading}
        cardHeight={cardHeight}
        cardWidth={cardWidth}
        CardComponent={PublicBotCardComponent}
        title={botType}
      />
    </Wrapper>
  );
};

export default DcaBotsSection;

interface PublicBotCardComponentProps {
  data: BotTemplateData;
  extraData?: DcaBotsType;
  index: number;
  asRow?: boolean;
}

const PublicBotCardComponent = ({
  data,
  extraData,
  index,
  asRow,
}: PublicBotCardComponentProps) => {
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const { changeStep } = useContext(OnboardingContext)!;
  const track = useOnboardingAnalytics();
  const demo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const guided = useQuery().get("guided") === "1";
  const { account } = useOnboardingPracticeAccount(data.exchange);
  const prepareAccount = demo && guided && !account;

  const tradingMethodCategory = extraData
    ? TRADING_CATEGORY_TO_CATEGORY_CONST[
        SECTION_TYPE_TO_TRADING_CATEGORY[extraData]
      ]?.toString()
    : "";

  return (
    <PublicBotCard
      botData={data}
      asRow={asRow}
      prepareAccountOnClick={prepareAccount}
      onBotClick={() => {
        track("Public Bot Selected", {
          bot_uuid: data.uuid,
          exchange: data.exchange,
          rank: index + 1,
          category: extraData,
        });
        changeStep(
          prepareAccount
            ? OnboardingDetailedStep.CONNECT_EXCHANGE
            : OnboardingDetailedStep.DCA_BOT_OVERVIEW,
          {
            botUUID: data.uuid,
            category: tradingMethodCategory,
            botRank: index.toString(),
            ...(demo && guided
              ? {
                  exchange: data.exchange,
                  "account-id": account ? String(account.client_id) : "",
                  prepareDemo: prepareAccount ? "1" : "",
                  setup: prepareAccount ? "1" : "",
                }
              : {}),
          }
        );
      }}
      cardStyle={{
        width: isMobile ? MOBILE_BOT_CARD_WIDTH : ONBOARDING_BOT_CARD_WIDTH,
        height: isMobile ? MOBILE_BOT_CARD_HEIGHT : BOT_CARD_HEIGHT,
      }}
    />
  );
};

const Wrapper = styled.div`
  width: 100%;
`;
const PhoneSection = styled.section`
  width: 100%;
  margin-bottom: 24px;
`;
const PhoneSectionTitle = styled.h2`
  margin: 0 0 8px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const PhoneTiles = styled.div`
  display: flex;
  gap: 12px;
  margin: 0 -${ONBOARDING_PHONE_GUTTER}px;
  padding: 0 ${ONBOARDING_PHONE_GUTTER}px 4px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 ${ONBOARDING_PHONE_GUTTER}px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const PhoneTile = styled.div`
  flex: 0 0 84%;
  min-width: 0;
  overflow: hidden;
  scroll-snap-align: start;
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  border-radius: 12px;
  background: ${(p) => p.theme.colors.cardBgColor};
`;
